import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Share2, Camera, ExternalLink, Upload, FileCheck2, Trash2, ChevronDown } from "lucide-react";
import { api, getRunnerToken, setRunnerToken, shareImage, API, openExternal } from "../lib/api";
import { PageShell } from "../components/PageShell";
import { AppText, Button, Card, Medal, Sticker, Badge, Toggle } from "../components/ui";
import { PLAN_LABEL, NOISE_OPTIONS, TALK_OPTIONS, PAIN_OPTIONS, ALERT_OPTIONS, EFFORT_LABELS, PAIN_ZONES } from "../lib/programUtils";
import { Seo } from "../components/Seo";
import { GainageSection } from "../components/Gainage";
import { ActivityReport } from "../components/ActivityReport";

export default function Programme() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offline, setOffline] = useState(false);
  const hasToken = !!getRunnerToken();

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const d = await api.runnerProgram();
      setData(d);
      try { localStorage.setItem("km1_last_program", JSON.stringify(d)); } catch {}
      setOffline(false);
    }
    catch (e) {
      const cached = localStorage.getItem("km1_last_program");
      if (cached && !navigator.onLine) { setData(JSON.parse(cached)); setOffline(true); }
      else setError(e.message || "Erreur");
    }
    finally { setLoading(false); }
  };
  useEffect(() => { if (hasToken) load(); else setLoading(false); }, []); // eslint-disable-line

  if (loading) return <PageShell><div className="py-24 flex justify-center"><span className="h-8 w-8 animate-spin rounded-full border-4 border-km-coral border-t-transparent" /></div></PageShell>;

  if (!hasToken || error) {
    const profile = params.get("profile");
    const plan = params.get("plan") || "3m";
    if (profile) return <Paywall profile={profile} plan={plan} />;
    return (
      <PageShell>
        <Seo title="Mon programme" description="Ton espace membre KM01." noindex />
        <Card className="mt-8 flex flex-col items-center text-center gap-4 py-10">
          <Medal size={96} />
          <AppText variant="title">Mon programme</AppText>
          <AppText variant="body" className="text-km-ontertiary max-w-md">Ton programme t'attend derrière ton lien de connexion. Pas encore de programme ? Commence par ton profil, c'est gratuit.</AppText>
          <div className="flex flex-col gap-2 w-full max-w-xs">
            <Button title="Me connecter" onClick={() => navigate("/connexion")} />
            <Button title="Faire mon profil (5 min)" variant="secondary" onClick={() => navigate("/profil")} />
          </div>
        </Card>
      </PageShell>
    );
  }

  return <Member data={data} reload={load} navigate={navigate} offline={offline} />;
}

function Paywall({ profile, plan }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTrial, setShowTrial] = useState(false);
  const [code, setCode] = useState("");
  const [trialEmail, setTrialEmail] = useState("");
  const p = PLAN_LABEL[plan] || PLAN_LABEL["3m"];
  const ref = localStorage.getItem("km1_ref");

  const pay = async () => {
    setLoading(true);
    try {
      const res = await api.checkout(profile, plan, email.trim() || undefined, ref);
      if (res.url) window.location.assign(res.url);
    } catch (e) { toast.error(e.message || "Paiement impossible"); setLoading(false); }
  };
  const redeem = async () => {
    if (!code.trim()) { toast.error("Entre ton code d'essai"); return; }
    setLoading(true);
    try {
      const res = await api.redeem(profile, code.trim(), trialEmail.trim() || undefined);
      if (res.session_token) { setRunnerToken(res.session_token); navigate("/programme"); window.location.reload(); }
    } catch (e) { toast.error(e.message || "Code invalide"); setLoading(false); }
  };
  const field = "w-full min-h-[52px] bg-white rounded-2xl border-2 border-km-border px-4 font-text font-semibold text-[17px] text-km-ink outline-none focus:border-km-coral";

  return (
    <PageShell>
      <Seo title="Démarrer mon programme" description="Débloque ton programme KM01." noindex />
      <Card className="mt-6 relative border-2 border-km-coral flex flex-col items-center gap-2 pt-8">
        <Sticker text="Le plus choisi" tilt={-4} className="absolute -top-3 left-4" />
        <Medal value={plan === "6m" ? "21" : "05"} size={84} />
        <AppText variant="title">{p.title}</AppText>
        <AppText variant="display" className="text-km-coral">{p.price}</AppText>
        <AppText variant="caption" className="text-km-muted text-center">{p.sub}</AppText>
        <input data-testid="paywall-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="ton@email.fr (facultatif)" className={field + " mt-3"} />
        <Button title={`Payer ${p.price} — c'est parti`} onClick={pay} loading={loading} testID="paywall-pay" className="w-full mt-1" />
        <AppText variant="caption" className="text-km-muted text-center mt-1">Un code promo ? Tu pourras le saisir sur la page de paiement. Paiement sécurisé par Stripe. Tu reçois ensuite un lien de connexion par email — pas de mot de passe à retenir.</AppText>
      </Card>
      <div className="mt-4 flex flex-col gap-2 items-center">
        <button onClick={() => navigate("/connexion")} className="min-h-[44px] font-text font-bold text-km-coral">Déjà membre ? Me connecter</button>
        <button onClick={() => setShowTrial((v) => !v)} data-testid="trial-toggle" className="min-h-[44px] font-text text-km-muted">J'ai un code d'essai gratuit</button>
      </div>
      {showTrial ? (
        <Card className="mt-2 flex flex-col gap-2">
          <AppText variant="label" className="text-km-muted">Ton code d'essai</AppText>
          <input data-testid="trial-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ex : KM01-15J" className={field} />
          <input data-testid="trial-email" value={trialEmail} onChange={(e) => setTrialEmail(e.target.value)} type="email" placeholder="ton@email.fr" className={field} />
          <Button title="Activer mon essai" onClick={redeem} loading={loading} testID="trial-submit" />
        </Card>
      ) : null}
    </PageShell>
  );
}

function Member({ data, reload, navigate, offline }) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [unread, setUnread] = useState(false);
  const prog = data.program || {};
  const runner = data.runner || {};
  const current = data.current ? { ...data.current, ...(typeof data.current.text === "object" && data.current.text ? data.current.text : {}) } : null;
  const sessionLabel = current?.session?.body?.label;
  const badges = (prog.badges || []).map((b) => b.key || b);
  const milestones = prog.milestones || [];
  const next = prog.next_milestone;
  const logout = async () => { try { await api.runnerLogout(); } catch {} setRunnerToken(null); localStorage.removeItem("km1_last_program"); navigate("/"); window.location.reload(); };

  return (
    <PageShell>
      <Seo title="Mon programme" description="Ton espace membre KM01." noindex />
      {offline ? <div className="mt-4 rounded-2xl bg-km-sunny/60 border border-km-borderstrong px-4 py-2" data-testid="offline-banner"><AppText variant="caption" className="font-bold">Mode hors ligne — voici ta dernière séance enregistrée.</AppText></div> : null}
      {unread ? (
        <button onClick={() => document.getElementById("chat-anchor")?.scrollIntoView({ behavior: "smooth" })} data-testid="new-message-banner"
          className="mt-4 w-full flex items-center gap-2 rounded-2xl bg-km-coral text-white px-4 py-3 shadow-coral">
          <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" /></span>
          <AppText variant="bodyStrong" className="text-white flex-1 text-left">Nouveau message d'Éric — appuie pour lire</AppText>
        </button>
      ) : null}
      <div className="flex items-center justify-between mt-4">
        <AppText variant="display">Mon programme</AppText>
        <button onClick={logout} data-testid="logout" className="flex items-center gap-1 text-km-muted font-text font-bold"><LogOut size={18} /> Se déconnecter</button>
      </div>
      <AppText variant="caption" className="text-km-muted mt-1">Semaine {prog.week} · {prog.phase_label || `phase ${prog.phase}`} · {data.done_count || prog.done_count || 0} séance(s) faite(s)</AppText>

      {data.analysis_url ? <AnalysisBanner url={data.analysis_url} motor={data.profile?.motor} /> : null}

      {prog.needs_profile ? (
        <div className="mt-4 rounded-2xl bg-km-sunny/60 border border-km-borderstrong px-4 py-3" data-testid="needs-profile-banner">
          <AppText variant="bodyStrong">Personnalise ton programme</AppText>
          <AppText variant="caption" className="text-km-muted mt-1">Tu as démarré en mode découverte. Importe ton bilan de foulée ci-dessous — ou refais ton profil — pour caler ton programme sur TA foulée.</AppText>
        </div>
      ) : null}

      {prog.locked ? (
        <Card className="mt-4 border-2 border-km-coral">
          <AppText variant="subtitle">{prog.expired ? "Ton accès est terminé" : "Accès suspendu"}</AppText>
          <AppText variant="body" className="mt-2 text-km-ontertiary">Ton espace reste lisible — carnet, historique, erreurs — mais la nouvelle séance n'est plus générée. Prolonge quand tu veux, on repart exactement où tu en étais.</AppText>
          <Button title="Programme 10K — 3 mois, 14,90 €" onClick={() => extend(prog, "3m")} className="mt-3 w-full" />
          <Button title="Programme 21/42K — 6 mois, 24,90 €" variant="secondary" onClick={() => extend(prog, "6m")} className="mt-2 w-full" />
        </Card>
      ) : data.awaiting_review ? (
        <Card className="mt-4 flex flex-col items-center text-center gap-2 py-8">
          <Medal size={72} />
          <AppText variant="subtitle">Ta séance est chez Éric</AppText>
          <AppText variant="body" className="text-km-ontertiary max-w-md">Ta prochaine séance est écrite ; l'entraîneur la relit avant de te l'envoyer. Tu reçois un email dès qu'elle est prête — en général dans la journée.</AppText>
        </Card>
      ) : current ? (
        <Card className="mt-4 border-[3px] border-km-coral shadow-coral p-5 sm:p-6" testID="session-card">
          <div className="flex items-center gap-2"><span className="inline-flex h-2.5 w-2.5 rounded-full bg-km-coral" /><AppText variant="label" className="text-km-coral">Ta séance du jour</AppText></div>
          <AppText variant="display" className="mt-2 text-[28px] sm:text-[34px] leading-tight">{current.title || `Semaine ${prog.week} · Séance`}</AppText>
          {sessionLabel ? <div className="mt-3 rounded-xl bg-km-coralt px-3 py-2" data-testid="session-structure"><AppText variant="bodyStrong" className="text-km-oncoralt">{sessionLabel}</AppText></div> : null}
          {current.intro ? <AppText variant="body" className="mt-2 text-km-ontertiary">{current.intro}</AppText> : null}
          {current.body ? <AppText variant="body" className="mt-2 whitespace-pre-line">{typeof current.body === "string" ? current.body : JSON.stringify(current.body)}</AppText> : null}
          {current.closing ? <AppText variant="body" className="mt-2 text-km-ontertiary italic">{current.closing}</AppText> : null}
          <Button title="Séance faite" onClick={() => setFeedbackOpen(true)} testID="session-done" className="mt-4 w-full" />
        </Card>
      ) : (
        <Card className="mt-4"><AppText variant="body" className="text-km-ontertiary">Ta première séance validée apparaîtra ici.</AppText></Card>
      )}

      {/* Ma semaine type */}
      <WeekPlanSection prog={prog} reload={reload} />
      <ActivityReport inactivity={data.inactivity} reload={reload} />

      {feedbackOpen ? <FeedbackSheet onClose={() => setFeedbackOpen(false)} onDone={() => { setFeedbackOpen(false); reload(); }} triggered={data.triggered} /> : null}

      {/* Médailles, erreur de la semaine, carnet — repliés par défaut pour une page courte */}
      <Collapsible id="medals" title="Tes médailles" summary={`${badges.length} / ${milestones.length} · prochaine : ${next || "—"}`}>
        <Card className="flex flex-wrap justify-center gap-3" testID="medal-shelf">
          {milestones.map((mk) => {
            const key = String(mk).replace("KM", "");
            const won = badges.includes(mk);
            return (
              <div key={mk} className="w-[28%] min-w-[90px] flex flex-col items-center gap-1 py-2">
                <div className={won ? "" : "opacity-25 grayscale"}><Medal value={key} size={72} /></div>
                <AppText variant="caption" className={won ? "text-km-ink font-bold" : "text-km-muted"}>{mk}</AppText>
                {mk === next && !won ? <Badge text="Objectif" tone="brand" /> : null}
              </div>
            );
          })}
        </Card>
      </Collapsible>

      {data.error_of_week ? (
        <Collapsible id="error" title="L'erreur de la semaine" summary={`« ${data.error_of_week.phrase} »`}>
          <Card style={{ transform: "rotate(-1deg)" }}>
            <AppText variant="bodyStrong" className="text-km-muted line-through">« {data.error_of_week.phrase} »</AppText>
            <AppText variant="bodyStrong" className="mt-1">{data.error_of_week.answer}</AppText>
          </Card>
        </Collapsible>
      ) : null}

      <Collapsible id="journal" title="Depuis ton KM01" summary={`${data.log?.total_km ?? data.total_km ?? 0} km · ${data.done_count || prog.done_count || data.log?.run_count || 0} séances`}>
        <Card className="flex flex-col gap-4">
          <div className="flex gap-6">
            <div><AppText variant="display" className="text-km-coral">{(data.log?.total_km ?? data.total_km ?? 0)}</AppText><AppText variant="caption" className="text-km-muted">km</AppText></div>
            <div><AppText variant="display">{data.done_count || prog.done_count || data.log?.run_count || 0}</AppText><AppText variant="caption" className="text-km-muted">séances</AppText></div>
            {data.streak && (data.streak.current ?? data.streak.weeks) != null ? <div><AppText variant="display">{data.streak.current ?? data.streak.weeks}</AppText><AppText variant="caption" className="text-km-muted">série (sem.)</AppText></div> : null}
          </div>
          <KmChart weeks={data.log?.weeks || []} />
        </Card>
      </Collapsible>

      {/* Analyse de foulée liberetafoulee.fr */}
      <GaitAnalysisSection analysis={prog.gait_analysis} url={data.analysis_url} reload={reload} />

      {/* Gainage vertical + renforcement : ouverts les 10 premiers jours, repliés ensuite pour garder la page courte */}
      {prog.first_days ? (
        <>
          <GainageSection stabilityScore={data.profile?.stability?.score} motor={data.profile?.motor?.label} />
          <StrengthSection strength={prog.strength} coachRenfo={prog.coach_renfo} thigh={prog.thigh_strength} />
        </>
      ) : (
        <>
          <Collapsible id="gainage" title="Ton gainage vertical" summary="10 à 12 min, debout — tous les jours tant que le test pieds alignés tremble, puis 2 à 3 fois par semaine">
            <GainageSection stabilityScore={data.profile?.stability?.score} motor={data.profile?.motor?.label} bare />
          </Collapsible>
          <Collapsible id="strength" title="Ton renforcement" summary={prog.thigh_strength?.title || "renfo cuisses et zones sensibles"}>
            <StrengthSection strength={prog.strength} coachRenfo={prog.coach_renfo} thigh={prog.thigh_strength} bare />
          </Collapsible>
        </>
      )}

      {/* Chat coach */}
      <ChatSection seenKey={runner.email || "me"} onUnread={setUnread} />

      {/* Compte */}
      <div className="mt-8">
        <div className="w-10 h-1.5 rounded-full bg-km-coral mb-2" />
        <AppText variant="title" className="mb-4">Mon compte</AppText>
        <Card className="flex flex-col gap-2">
          <AppText variant="body">Email : <strong>{runner.email}</strong></AppText>
          {runner.access_code ? (
            <div data-testid="account-access-code" className="mt-1 p-3 rounded-xl bg-km-tertiary flex flex-col gap-0.5">
              <AppText variant="caption" className="text-km-muted">Ton code d'accès personnel (pour te connecter)</AppText>
              <AppText variant="bodyStrong" className="tracking-[0.3em] text-[22px] text-km-ink">{runner.access_code}</AppText>
              <AppText variant="caption" className="text-km-muted">Garde-le pour toi : email + ce code = accès à ton programme.</AppText>
            </div>
          ) : null}
          <AppText variant="body">Plan : <strong>{PLAN_LABEL[prog.plan]?.title || prog.plan}</strong></AppText>
          {prog.days_left != null ? <AppText variant="body">Fin d'accès dans <strong>{prog.days_left} jour(s)</strong></AppText> : null}
          <PushToggle />
          {data.referral?.link ? (
            <div className="mt-2 p-3 rounded-xl bg-km-tertiary">
              <AppText variant="bodyStrong">Parraine un ami</AppText>
              <AppText variant="caption" className="text-km-muted">Il gagne -{data.referral.percent || 20} % sur son programme, toi +30 jours d'accès.</AppText>
              <AppText variant="caption" className="mt-1 break-all text-km-coral">{data.referral.link}</AppText>
            </div>
          ) : null}
          <Button title="Se déconnecter" variant="secondary" size="md" onClick={logout} className="mt-2" />
        </Card>
      </div>
    </PageShell>
  );

  async function extend(program, plan) {
    try {
      const res = await api.checkout(data.profile?.id || prog.profile_id, plan, runner.email);
      if (res.url) window.location.assign(res.url);
    } catch (e) { toast.error(e.message || "Erreur"); }
  }
}

function KmChart({ weeks }) {
  const rows = (weeks || []).slice(-10);
  if (rows.length < 2) return <AppText variant="caption" className="text-km-muted">Ta courbe de kilomètres apparaîtra après quelques séances.</AppText>;
  const W = 300, H = 90, pad = 6;
  const max = Math.max(1, ...rows.map((w) => w.km));
  const bw = (W - pad * 2) / rows.length;
  const points = rows.map((w, i) => {
    const x = pad + bw * i + bw / 2;
    const y = H - pad - (w.km / max) * (H - pad * 2);
    return { x, y, w };
  });
  const line = points.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  return (
    <div data-testid="km-chart" className="w-full">
      <AppText variant="label" className="text-km-muted mb-2">Kilomètres par semaine</AppText>
      <svg viewBox={`0 0 ${W} ${H + 18}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        {points.map((p, i) => (
          <g key={i}>
            <rect x={p.x - bw * 0.3} y={p.y} width={bw * 0.6} height={H - pad - p.y} rx="3" fill="#FFE3DA" />
            <text x={p.x} y={H + 12} textAnchor="middle" fontSize="8" fill="#8A817C" fontFamily="Nunito">{p.w.label}</text>
            {p.w.km > 0 ? <text x={p.x} y={p.y - 3} textAnchor="middle" fontSize="8" fill="#2B2B2B" fontFamily="Nunito" fontWeight="700">{p.w.km}</text> : null}
          </g>
        ))}
        <path d={line} fill="none" stroke="#FF6B4A" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => <circle key={`c${i}`} cx={p.x} cy={p.y} r="3" fill="#FF6B4A" />)}
      </svg>
    </div>
  );
}

function PushToggle() {
  const [state, setState] = useState("idle");
  const enable = async () => {
    setState("loading");
    try {
      const { enablePush } = await import("../lib/push");
      const res = await enablePush();
      if (res === "subscribed") {
        setState("on");
        try { await api.pushTest(); toast.success("Rappels activés — notification de test envoyée."); }
        catch { toast.success("Rappels activés."); }
      } else if (res === "denied") { setState("denied"); toast.message("Notifications refusées — tu recevras les rappels par email."); }
      else { setState("unsupported"); toast.message("Notifications non disponibles ici — rappels par email."); }
    } catch { setState("idle"); toast.error("Impossible d'activer les rappels"); }
  };
  return (
    <div className="mt-2 p-3 rounded-xl bg-km-tertiary flex items-center justify-between gap-3">
      <div className="flex-1">
        <AppText variant="bodyStrong">Rappels de séance</AppText>
        <AppText variant="caption" className="text-km-muted">{state === "on" ? "Activés sur cet appareil." : "Reçois une notification la veille. Sinon, on te rappelle par email."}</AppText>
      </div>
      <Button title={state === "on" ? "Activés" : "Activer"} size="md" variant={state === "on" ? "sage" : "secondary"} loading={state === "loading"} onClick={enable} testID="push-enable" disabled={state === "on"} />
    </div>
  );
}

function Collapsible({ id, title, summary, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6" data-testid={`collapsible-${id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid={`collapsible-${id}-toggle`}
        className="w-full flex items-center gap-3 text-left min-h-[52px] px-4 rounded-2xl bg-white border-2 border-km-border hover:border-km-coral transition-colors">
        <div className="w-2.5 h-2.5 rounded-full bg-km-coral shrink-0" />
        <div className="flex-1 min-w-0">
          <AppText variant="bodyStrong">{title}</AppText>
          {!open && summary ? <AppText variant="caption" className="text-km-muted truncate">{summary}</AppText> : null}
        </div>
        <ChevronDown size={20} className={`shrink-0 text-km-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}

function ChatSection({ seenKey = "me", onUnread }) {
  const [chat, setChat] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const anchorRef = React.useRef(null);
  const storeKey = `km1_chat_seen_${seenKey}`;

  const computeUnread = (messages) => {
    const coach = messages.filter((m) => m.role === "coach");
    if (!coach.length) return false;
    const last = coach[coach.length - 1].created_at || "";
    const seen = localStorage.getItem(storeKey) || "";
    return last > seen;
  };
  const markSeen = (messages) => {
    const coach = messages.filter((m) => m.role === "coach");
    if (coach.length) localStorage.setItem(storeKey, coach[coach.length - 1].created_at || new Date().toISOString());
    onUnread && onUnread(false);
  };

  const load = async () => {
    try {
      const d = await api.runnerChat();
      setChat(d);
      onUnread && onUnread(computeUnread(d.messages || []));
      return d;
    } catch { setChat({ messages: [], pending: 0 }); return null; }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Marquer comme lu quand la section devient visible.
  useEffect(() => {
    if (!anchorRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && chat?.messages) markSeen(chat.messages);
    }, { threshold: 0.4 });
    obs.observe(anchorRef.current);
    return () => obs.disconnect();
  }, [chat]); // eslint-disable-line

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    const mine = text.trim();
    try {
      const r = await api.runnerAsk(mine);
      setText("");
      toast.success(r?.auto ? "Réponse immédiate de l'assistant — Éric garde un œil" : "Question envoyée à Éric");
      const d = await load();
      if (d?.messages) markSeen(d.messages);
    } catch (e) { toast.error(e.message || "Erreur"); } finally { setSending(false); }
  };
  const msgs = chat?.messages || [];
  return (
    <div className="mt-8" data-testid="chat-section" id="chat-anchor" ref={anchorRef}>
      <div className="w-10 h-1.5 rounded-full bg-km-coral mb-2" />
      <AppText variant="title" className="mb-1">Une question ?</AppText>
      <AppText variant="caption" className="text-km-muted mb-4">Les questions sur la méthode reçoivent une réponse immédiate ; pour le reste, Éric lit ton message et te répond personnellement — ici et par email.</AppText>
      <Card className="flex flex-col gap-3">
        {msgs.length === 0 ? <AppText variant="body" className="text-km-muted">Pose ta question sur ta séance, une douleur, tes chaussures…</AppText> : null}
        <div className="flex flex-col gap-2 max-h-[320px] overflow-y-auto">
          {msgs.map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.role === "coach" ? "self-start bg-km-sage/15 border border-km-sage/40" : "self-end bg-km-coralt"}`} data-testid={m.auto ? "chat-msg-auto" : `chat-msg-${m.role}`}>
              {m.role === "coach" ? <AppText variant="label" className="text-km-sage mb-0.5">{m.auto ? "Assistant KM01 · réponse immédiate" : "Éric"}</AppText> : null}
              <AppText variant="body" className="whitespace-pre-line text-[16px]">{m.text}</AppText>
            </div>
          ))}
        </div>
        {chat?.pending ? <AppText variant="caption" className="text-km-muted italic" data-testid="chat-pending">Ta question est chez Éric — réponse en général dans la journée.</AppText> : null}
        <div className="flex gap-2 items-end">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Écris à ton entraîneur…" data-testid="chat-input"
            className="flex-1 bg-white rounded-2xl border-2 border-km-border px-4 py-2 font-text text-[16px] outline-none focus:border-km-coral resize-none" />
          <Button title="Envoyer" size="md" onClick={send} loading={sending} testID="chat-send" />
        </div>
      </Card>
    </div>
  );
}

function FeedbackSheet({ onClose, onDone, triggered }) {
  const [noise, setNoise] = useState("jamais");
  const [talk, setTalk] = useState("oui_facilement");
  const [pain, setPain] = useState("non");
  const [painZone, setPainZone] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [effort, setEffort] = useState(4);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [watch, setWatch] = useState(null);
  const [reading, setReading] = useState(false);
  const [loading, setLoading] = useState(false);

  const readWatch = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReading(true); setWatch(null);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const d = await api.runnerWatchRead(b64);
      setWatch(d);
      if (d.distance_km != null) setDistance(String(d.distance_km).replace(".", ","));
      if (d.duration_min != null) setDuration(String(d.duration_min).replace(".", ","));
      toast.success(d.is_run === false ? "Lu, mais ça ne ressemble pas à une course" : `Capture lue (confiance ${d.confidence})`);
    } catch (err) { toast.error(err.message || "Lecture impossible — saisis les km à la main"); }
    finally { setReading(false); }
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.runnerFeedback({
        noise, talk, pain, pain_zone: pain !== "non" ? painZone : null, alerts, effort,
        distance_km: distance ? parseFloat(distance.replace(",", ".")) : null,
        duration_min: duration ? parseFloat(duration.replace(",", ".")) : null,
        watch: watch || null,
      });
      toast.success("Séance validée !");
      onDone();
    } catch (e) { toast.error(e.message || "Erreur"); setLoading(false); }
  };
  const Row = ({ label, options, value, onChange }) => (
    <div className="flex flex-col gap-1">
      <AppText variant="label" className="text-km-muted">{label}</AppText>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o.value} onClick={() => onChange(o.value)} className={`px-3 py-2 rounded-full border-2 font-text font-bold text-[14px] ${value === o.value ? "border-km-coral bg-km-coralt text-km-oncoralt" : "border-km-border bg-white"}`}>{o.label}</button>
        ))}
      </div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="feedback-overlay">
      <div className="bg-km-cream rounded-km w-full max-w-[560px] max-h-[90vh] overflow-y-auto p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()} data-testid="feedback-sheet">
        <AppText variant="title">Ta séance est faite</AppText>
        <AppText variant="body" className="text-km-ontertiary -mt-1">Dis-nous l'essentiel — ça affine ta prochaine séance.</AppText>

        {/* Distance + durée — en premier, bien visibles */}
        <div className="flex gap-3">
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <AppText variant="label" className="text-km-ink">Distance (km)</AppText>
            <input value={distance} onChange={(e) => setDistance(e.target.value)} inputMode="decimal" placeholder="ex : 3,2" className="w-full min-w-0 min-h-[56px] bg-white rounded-2xl border-2 border-km-border px-4 font-text font-bold text-[22px] text-km-ink outline-none focus:border-km-coral" data-testid="distance-input" />
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <AppText variant="label" className="text-km-ink">Durée (min)</AppText>
            <input value={duration} onChange={(e) => setDuration(e.target.value)} inputMode="numeric" placeholder="ex : 28" className="w-full min-w-0 min-h-[56px] bg-white rounded-2xl border-2 border-km-border px-4 font-text font-bold text-[22px] text-km-ink outline-none focus:border-km-coral" data-testid="duration-input" />
          </div>
        </div>

        {/* Ressenti — bien visible */}
        <div className="flex flex-col gap-1.5 rounded-2xl bg-white border-2 border-km-border p-3">
          <AppText variant="label" className="text-km-ink">Ton ressenti · <span className="text-km-coral">{effort}/7 — {EFFORT_LABELS[effort]}</span></AppText>
          <input type="range" min="1" max="7" value={effort} onChange={(e) => setEffort(Number(e.target.value))} className="w-full h-2 accent-km-coral" data-testid="effort-slider" />
          <div className="flex justify-between"><AppText variant="caption" className="text-km-muted">Très facile</AppText><AppText variant="caption" className="text-km-muted">Très dur</AppText></div>
        </div>

        {/* Lecture de montre */}
        <div className="flex flex-col gap-2 rounded-2xl bg-white border border-km-border p-3">
          <AppText variant="label" className="text-km-coral">Ta montre / appli (facultatif)</AppText>
          <AppText variant="caption" className="text-km-muted">Envoie une capture (Garmin, Strava, Apple Watch…). L'IA lit les chiffres pour toi — l'image n'est jamais conservée.</AppText>
          <label className="self-start">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-km-ink text-km-cream font-bold cursor-pointer">
              <Camera size={16} /> {reading ? "Lecture en cours…" : "Lire ma capture"}
            </span>
            <input type="file" accept="image/*" onChange={readWatch} className="hidden" data-testid="watch-input" disabled={reading} />
          </label>
          {watch ? (
            <AppText variant="caption" className="text-km-ontertiary" data-testid="watch-result">
              Lu : {watch.distance_km != null ? `${String(watch.distance_km).replace(".", ",")} km` : "—"}{watch.duration_min != null ? ` · ${watch.duration_min} min` : ""}{watch.pace ? ` · ${watch.pace}` : ""}{watch.avg_hr ? ` · ${watch.avg_hr} bpm` : ""}. Corrige si besoin ci-dessus.
            </AppText>
          ) : null}
        </div>

        {/* Qualitatif */}
        <Row label="Ta foulée a fait du bruit ?" options={NOISE_OPTIONS} value={noise} onChange={setNoise} />
        <Row label="Tu pouvais parler ?" options={TALK_OPTIONS} value={talk} onChange={setTalk} />
        <Row label="Une douleur ?" options={PAIN_OPTIONS} value={pain} onChange={(v) => { setPain(v); if (v === "non") setPainZone(null); }} />
        {pain !== "non" ? <Row label="Où ?" options={PAIN_ZONES} value={painZone} onChange={setPainZone} /> : null}
        <div className="flex gap-2">
          <Button title="Annuler" variant="secondary" size="md" onClick={onClose} className="flex-1" />
          <Button title="Valider" onClick={submit} loading={loading} size="md" testID="feedback-submit" className="flex-1" />
        </div>
        {loading ? <AppText variant="caption" className="text-km-muted italic text-center" data-testid="feedback-saving">On enregistre et on écrit ta prochaine séance… 10 à 20 secondes.</AppText> : null}
      </div>
    </div>,
    document.body
  );
}

const MOTOR_FR = { terrien: "terrien", aerien: "aérien", intermediaire: "intermédiaire" };
const DAY_ABBR = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const ITEM_STYLE = {
  course: "bg-km-coralt text-km-oncoralt",
  sortie_longue: "bg-km-coral text-white",
  ef: "bg-km-coralt text-km-oncoralt",
  technique: "bg-km-sage/25 text-km-ink",
  marche: "bg-km-sage/25 text-km-ink",
  repos_actif: "bg-km-tertiary text-km-ontertiary",
  gainage: "bg-km-sunny/60 text-km-ink",
  renfo: "bg-km-sage/20 text-km-ink",
  renfo_coach: "bg-km-sunny/70 text-km-ink",
  autre: "bg-km-tertiary text-km-muted",
  repos: "bg-transparent text-km-muted",
};

function AnalysisBanner({ url, motor }) {
  const label = motor?.label ? MOTOR_FR[motor.label] || motor.label : null;
  return (
    <div className="mt-4 rounded-2xl bg-km-sage/12 border border-km-sage/40 px-4 py-3 flex flex-col gap-2" data-testid="analysis-banner">
      <AppText variant="bodyStrong">Confirme ton profil moteur{label ? ` (probablement ${label})` : ""}</AppText>
      <AppText variant="caption" className="text-km-muted">Le questionnaire donne une forte probabilité, jamais une certitude. L'analyse de foulée liberetafoulee.fr valide ton profil et affine ton programme.</AppText>
      <Button title="Analyser ma foulée sur liberetafoulee.fr" variant="sage" size="md" onClick={() => openExternal(url)} testID="analysis-link" icon={<ExternalLink size={16} strokeWidth={2.5} />} />
    </div>
  );
}

function StrengthSection({ strength, coachRenfo, thigh, bare }) {
  if (!strength) return null;
  const targeted = strength.targeted && (strength.groups || []).length > 0;
  return (
    <div className={bare ? "" : "mt-8"} data-testid="strength-section">
      {bare ? null : <><div className="w-10 h-1.5 rounded-full bg-km-coral mb-2" />
      <AppText variant="title" className="mb-1">Ton renforcement</AppText>
      <AppText variant="caption" className="text-km-muted mb-4">{strength.duration_min ? `${strength.duration_min} min · ` : ""}{targeted ? "adapté à tes zones sensibles." : "à garder chaque semaine."}</AppText></>}
      {thigh && !coachRenfo ? (
        <Card className="flex flex-col gap-2 mb-3 border-2 border-km-coral/40" testID="thigh-strength">
          <AppText variant="label" className="text-km-coral">{thigh.title}{thigh.palier ? " · au moins 1 fois par semaine" : ""}</AppText>
          <ul className="list-disc pl-5">
            {thigh.exercises.map((e, i) => <li key={i}><AppText variant="body" className="text-km-ontertiary">{e}</AppText></li>)}
          </ul>
          <AppText variant="caption" className="text-km-muted">{thigh.note}</AppText>
          {thigh.tool ? (
            <a href={thigh.tool.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 self-start text-km-coral font-bold min-h-[40px]" data-testid="multigrip-link">
              <ExternalLink size={16} /> Conseil d'Éric : {thigh.tool.label}
            </a>
          ) : null}
        </Card>
      ) : null}
      <Card className="flex flex-col gap-3">
        {targeted ? <AppText variant="body" className="text-km-ontertiary">{strength.cue}</AppText> : null}
        {coachRenfo ? (
          <div className="rounded-xl bg-km-sunny/60 border border-km-borderstrong px-3 py-2" data-testid="coach-renfo-note">
            <AppText variant="caption" className="font-bold">Tu fais déjà du renfo avec ton coach : garde SA priorité. Ci-dessous, un simple complément — ne double pas la charge.</AppText>
          </div>
        ) : null}
        {targeted ? (strength.groups || []).map((g) => (
          <div key={g.zone} className="flex flex-col gap-1" data-testid={`strength-zone-${g.zone}`}>
            <AppText variant="label" className="text-km-coral">{g.label}</AppText>
            <ul className="list-disc pl-5">
              {g.exercises.map((e, i) => <li key={i}><AppText variant="body" className="text-km-ontertiary">{e}</AppText></li>)}
            </ul>
          </div>
        )) : (
          <ul className="list-disc pl-5">
            {(strength.exercises || []).map((e, i) => <li key={i}><AppText variant="body" className="text-km-ontertiary">{e}</AppText></li>)}
          </ul>
        )}
      </Card>
    </div>
  );
}

function WeekPlanSection({ prog, reload }) {
  const [days, setDays] = useState(prog.training_days || []);
  const [mins, setMins] = useState(prog.day_minutes || {});
  const [coachRenfo, setCoachRenfo] = useState(!!prog.coach_renfo);
  const [saving, setSaving] = useState(false);
  const plan = prog.week_plan;

  const toggleDay = (d) => setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b));
  const save = async () => {
    if (!days.length) { toast.error("Choisis au moins un jour"); return; }
    setSaving(true);
    try {
      const clean = {};
      days.forEach((d) => { const n = parseInt(mins[d], 10); if (n >= 10) clean[d] = n; });
      await api.runnerDays(days, prog.reminders !== false, clean, coachRenfo);
      toast.success("Semaine type mise à jour");
      reload();
    } catch (e) { toast.error(e.message || "Erreur"); } finally { setSaving(false); }
  };
  const field = "w-16 min-h-[40px] bg-white rounded-xl border-2 border-km-border px-2 font-text font-semibold text-[15px] text-km-ink outline-none focus:border-km-coral";

  return (
    <div className="mt-8" data-testid="week-plan-section">
      <div className="w-10 h-1.5 rounded-full bg-km-coral mb-2" />
      <AppText variant="title" className="mb-1">Ma semaine type</AppText>
      <AppText variant="caption" className="text-km-muted mb-4">Choisis tes jours et le temps que tu peux y consacrer. On place ta course, ton renfo et le repos autour de tes possibilités.</AppText>
      <Card className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <AppText variant="label" className="text-km-muted">Mes jours d'entraînement (jusqu'à 7 — le volume reste réparti selon ton niveau)</AppText>
          <div className="flex flex-wrap gap-2">
            {DAY_ABBR.map((n, d) => (
              <button key={d} onClick={() => toggleDay(d)} data-testid={`day-${d}`}
                className={`px-3 min-h-[40px] rounded-full border-2 font-text font-bold text-[14px] ${days.includes(d) ? "border-km-coral bg-km-coralt text-km-oncoralt" : "border-km-border bg-white text-km-ink"}`}>{n}</button>
            ))}
          </div>
        </div>
        {days.length ? (
          <div className="flex flex-col gap-2">
            <AppText variant="label" className="text-km-muted">Temps dispo par jour (minutes, facultatif)</AppText>
            <div className="flex flex-wrap gap-3">
              {days.map((d) => (
                <div key={d} className="flex items-center gap-2">
                  <AppText variant="caption" className="text-km-ontertiary w-8">{DAY_ABBR[d]}</AppText>
                  <input value={mins[d] ?? ""} onChange={(e) => setMins((m) => ({ ...m, [d]: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="min" className={field} data-testid={`min-${d}`} />
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3 rounded-xl bg-km-tertiary p-3">
          <div className="flex-1">
            <AppText variant="bodyStrong">Je fais déjà du renfo avec un coach</AppText>
            <AppText variant="caption" className="text-km-muted">On garde SON programme prioritaire — KM01 ne te met que des rappels.</AppText>
          </div>
          <button onClick={() => setCoachRenfo((v) => !v)} data-testid="coach-renfo-toggle"><Toggle checked={coachRenfo} /></button>
        </div>
        <Button title="Enregistrer ma semaine" onClick={save} loading={saving} size="md" testID="week-plan-save" />

        {plan ? (
          <>
            {(prog.week_plan_notes || []).map((n, i) => (
              <div key={i} className="rounded-xl bg-km-sunny/60 border border-km-borderstrong px-3 py-2" data-testid={`week-plan-note-${i}`}>
                <AppText variant="caption" className="font-bold">{n}</AppText>
              </div>
            ))}
            <div className="flex flex-col gap-1.5 border-t border-km-divider pt-3" data-testid="week-plan-grid">
              {plan.map((day) => (
                <div key={day.day} className="flex items-start gap-3">
                  <AppText variant="bodyStrong" className="w-24 shrink-0">{day.name}{day.minutes ? ` · ${day.minutes}′` : ""}</AppText>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {day.items.map((it, i) => (
                      <span key={i} className={`px-2.5 py-1 rounded-full text-[13px] font-text font-bold ${ITEM_STYLE[it.type] || ITEM_STYLE.autre}`}>{it.label}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {(prog.week_plan_advice || []).length ? (
              <div className="rounded-2xl bg-km-sage/10 border border-km-sage/40 px-4 py-3 flex flex-col gap-2" data-testid="week-plan-advice">
                <AppText variant="label" className="text-km-sage">Entre les séances — tes conseils</AppText>
                {prog.week_plan_advice.map((a, i) => <AppText key={i} variant="caption" className="text-km-ink leading-5" data-testid={`week-plan-advice-${i}`}>• {a}</AppText>)}
              </div>
            ) : null}
            <button className="flex items-center gap-2 self-start text-km-coral font-bold min-h-[44px]" data-testid="week-share"
              onClick={() => shareImage(`${API}/programs/${prog.id}/week.png`, "km01-semaine.png", "Ma semaine type KM01").then((r) => r === "downloaded" && toast.success("Image de ta semaine téléchargée")).catch(() => toast.error("Partage impossible"))}>
              <Share2 size={18} /> Télécharger / partager ma semaine
            </button>
          </>
        ) : (
          <AppText variant="caption" className="text-km-muted border-t border-km-divider pt-3">Choisis tes jours puis enregistre pour voir ta semaine type.</AppText>
        )}
      </Card>
    </div>
  );
}


const PILLAR_LABELS = { stability: "Stabilité", roll_off: "Déroulé du pied", posture: "Posture", coherence: "Cohérence motrice" };

function GaitAnalysisSection({ analysis, url, reload }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Envoie ton bilan au format PDF"); return; }
    setBusy(true);
    const id = toast.loading("Analyse de ton bilan en cours…");
    try {
      const r = await api.uploadGaitAnalysis(file);
      const mu = r?.motor_update;
      toast.success(mu ? (mu.changed ? `Bilan importé — profil confirmé « ${mu.to} » (le questionnaire estimait « ${mu.from} »)` : `Bilan importé — profil « ${mu.to} » confirmé`) : "Bilan importé — ton programme est affiné", { id, duration: 8000 });
      reload();
    } catch (err) {
      toast.error(err.message || "Import impossible", { id });
    } finally { setBusy(false); }
  };
  const remove = async () => { try { await api.deleteGaitAnalysis(); toast.success("Bilan retiré"); reload(); } catch (e) { toast.error(e.message || "Erreur"); } };

  const pillars = analysis?.pillars || {};
  const hasPillars = Object.values(pillars).some((v) => typeof v === "number");

  return (
    <div className="mt-8" data-testid="gait-analysis-section">
      <div className="w-10 h-1.5 rounded-full bg-km-sage mb-2" />
      <AppText variant="title" className="mb-1">Mon analyse de foulée</AppText>
      <AppText variant="caption" className="text-km-muted mb-4">Ton bilan liberetafoulee.fr permet au coach d'affiner encore plus ton programme.</AppText>
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={onFile} className="hidden" data-testid="gait-file-input" />

      {analysis ? (
        <Card className="flex flex-col gap-3" testID="gait-analysis-card">
          <div className="flex items-center gap-3">
            <FileCheck2 size={22} className="text-km-sage shrink-0" />
            <div className="flex-1">
              <AppText variant="bodyStrong">Bilan pris en compte{analysis.analysis_date ? ` · ${analysis.analysis_date}` : ""}</AppText>
              <AppText variant="caption" className="text-km-muted">Ton programme et ton coach IA s'adaptent à ce bilan.</AppText>
            </div>
            {typeof analysis.gait_score === "number" ? (
              <div className="text-right shrink-0">
                <AppText variant="title" className="text-km-coral leading-none">{analysis.gait_score}</AppText>
                <AppText variant="caption" className="text-km-muted">/100</AppText>
              </div>
            ) : null}
          </div>

          {analysis.summary ? <AppText variant="body" className="text-km-ontertiary">{analysis.summary}</AppText> : null}

          {hasPillars ? (
            <div className="flex flex-col gap-2">
              {Object.entries(PILLAR_LABELS).map(([k, label]) => typeof pillars[k] === "number" ? (
                <div key={k} className="flex items-center gap-2" data-testid={`gait-pillar-${k}`}>
                  <AppText variant="caption" className="w-32 shrink-0 text-km-ontertiary">{label}</AppText>
                  <div className="flex-1 h-2 rounded-full bg-km-tertiary overflow-hidden">
                    <div className="h-full rounded-full bg-km-sage" style={{ width: `${pillars[k]}%` }} />
                  </div>
                  <AppText variant="caption" className="w-8 text-right text-km-muted">{pillars[k]}</AppText>
                </div>
              ) : null)}
            </div>
          ) : null}

          {(analysis.priorities || []).length ? (
            <div>
              <AppText variant="label" className="text-km-coral mb-1">Priorités de ton bilan</AppText>
              <ul className="list-disc pl-5">
                {analysis.priorities.map((p, i) => <li key={i}><AppText variant="body" className="text-km-ontertiary">{p}</AppText></li>)}
              </ul>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3 pt-1">
            <button onClick={() => inputRef.current?.click()} disabled={busy} className="flex items-center gap-2 text-km-coral font-bold min-h-[40px]" data-testid="gait-replace">
              <Upload size={16} /> Remplacer le bilan
            </button>
            <button onClick={remove} disabled={busy} className="flex items-center gap-2 text-km-muted font-bold min-h-[40px]" data-testid="gait-remove">
              <Trash2 size={16} /> Retirer
            </button>
          </div>
        </Card>
      ) : (
        <Card className="flex flex-col gap-3" testID="gait-upload-card">
          <AppText variant="body" className="text-km-ontertiary">
            Tu as déjà fait ton analyse de foulée ? Importe ton bilan PDF : le coach IA l'utilisera pour adapter tes séances (stabilité, déroulé, renforcement) au plus près de toi.
          </AppText>
          <Button title="Importer mon bilan PDF" onClick={() => inputRef.current?.click()} loading={busy} size="md" testID="gait-upload-btn" icon={<Upload size={18} strokeWidth={2.5} />} />
          {url ? (
            <button onClick={() => openExternal(url)} className="flex items-center gap-2 self-start text-km-sage font-bold min-h-[40px]" data-testid="gait-get-link">
              <ExternalLink size={16} /> Pas encore de bilan ? Fais ton analyse sur liberetafoulee.fr
            </button>
          ) : null}
        </Card>
      )}
    </div>
  );
}

